import React from 'react';

import { defineMessages, FormattedMessage } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import CopyIcon from '@atlaskit/icon/core/copy';
import StatusInformationIcon from '@atlaskit/icon/core/status-information';
import Link from '@atlaskit/link';
import { fg } from '@atlaskit/platform-feature-flags';
import { Inline, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { useAIAnswerContext } from '../../../ai-answer-dialog/ai-answer-context';
import { onAIAnswerCopyButtonClicked } from '../../../ai-answer-dialog/analytics';
import { useSmartAnswerCardFeedbackOptions } from '../../../ai-answer-dialog/smart-answer-card/use-smart-answer-card-feedback-options';
import { useAnalytics } from '../../../common/controller/analytics';
import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../../common/controller/analytics/hooks/use-sain-usage';
import { AIFeedbackV2 } from '../../../common/ui/ai-feedback-v2';
import { ATLASSIAN_INTELLIGENCE_TRUST_LINK, QueryIntent } from '../../../constants';

import { OnlineDebuggerButton } from './online-debugger-button';

const messages = defineMessages({
	ai_content_disclaimer: {
		id: 'search_ai.ai_content_disclaimer',
		defaultMessage: 'Uses AI. Verify results.',
		description: 'Text for the AI content disclaimer',
	},
});

const copyIcon = () => (
	<CopyIcon color={token('color.icon.subtlest')} LEGACY_size="small" label="copy" />
);

type AIDisclaimerAndFeedbackProps = {
	showFeedbackButtons: boolean;
	showCopyButton?: boolean;
	copyText?: string;
	renderedContentRef?: React.RefObject<HTMLDivElement>;
};

const styles = cssMap({
	heightWrapper: {
		height: '24px',
	},
});

export function AIDisclaimerAndFeedback({
	showFeedbackButtons,
	showCopyButton,
	copyText,
	renderedContentRef,
}: AIDisclaimerAndFeedbackProps) {
	const { commonAttributes, smartAnswersRoute, isAnswered } = useAIAnswerContext();
	const [fireAnalyticsEvent] = useAnalytics();

	const { answerString } = commonAttributes;

	const feedbackOptions = useSmartAnswerCardFeedbackOptions();

	const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();

	const showOnlineDebuggerButton =
		isAnswered &&
		smartAnswersRoute === QueryIntent.NATURAL_LANGUAGE_QUERY &&
		fg('kd_sain_online_debugger');

	const onLinkClickHandler = (event: React.MouseEvent<HTMLAnchorElement>) => {
		// prevent clicks from bubbling up to the Pressable ancestor and causing the card to expand
		event.stopPropagation();
	};

	const onLikeButtonClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
		// prevent clicks from bubbling up to the Pressable ancestor and causing the card to expand
		event.stopPropagation();
		feedbackOptions.onLikeButtonClickHandler();
	};

	const onDislikeButtonClickHandler = (event: React.MouseEvent<HTMLButtonElement>) => {
		// prevent clicks from bubbling up to the Pressable ancestor and causing the card to expand
		event.stopPropagation();
		feedbackOptions.onDislikeButtonClickHandler();
	};

	const onCopyButtonClickHandler = async (event: React.MouseEvent<HTMLButtonElement>) => {
		// prevent clicks from bubbling up to the Pressable ancestor and causing the card to expand
		event.stopPropagation();
		fireSAINUsageEvent({ metCriteria: SainAction.COPY_CLICKED, commonAttributes });
		fireAnalyticsEvent(onAIAnswerCopyButtonClicked(commonAttributes));
		if (copyText) {
			await navigator.clipboard.writeText(copyText);
		} else {
			try {
				const node = renderedContentRef?.current;
				if (node) {
					const htmlBlob = new Blob([node.innerHTML], { type: 'text/html' });
					const textBlob = new Blob([answerString], { type: 'text/plain' });
					await navigator.clipboard.write([
						new ClipboardItem({
							'text/html': htmlBlob,
							'text/plain': textBlob,
						}),
					]);
				} else {
					await navigator.clipboard.writeText(answerString);
				}
			} catch {
				await navigator.clipboard.writeText(answerString);
			}
		}
	};

	return (
		<Inline alignBlock="end" grow="fill" spread="space-between" xcss={styles.heightWrapper}>
			<Inline alignBlock="center" space="space.025">
				<StatusInformationIcon
					color={token('color.icon.subtle', '#626F86')}
					label=""
					size="small"
					spacing="compact"
				/>
				<Text size="small">
					<Link
						appearance="subtle"
						href={ATLASSIAN_INTELLIGENCE_TRUST_LINK}
						onClick={onLinkClickHandler}
						rel="noopener noreferrer"
					>
						<FormattedMessage {...messages.ai_content_disclaimer} />
					</Link>
				</Text>
			</Inline>
			<Inline alignBlock="center" space="space.050">
				{showFeedbackButtons ? (
					<AIFeedbackV2
						entrypointId={feedbackOptions.entrypointId}
						userDetails={feedbackOptions.userDetails}
						attributes={feedbackOptions.attributes}
						attributesWithConsent={feedbackOptions.attributesWithConsent}
						onLikeButtonClickHandler={onLikeButtonClickHandler}
						onDislikeButtonClickHandler={onDislikeButtonClickHandler}
					/>
				) : null}
				{showCopyButton ? (
					<IconButton
						icon={copyIcon}
						label="Copy"
						onClick={onCopyButtonClickHandler}
						appearance="subtle"
						isTooltipDisabled={false}
						spacing="compact"
					/>
				) : null}
				{showOnlineDebuggerButton && <OnlineDebuggerButton />}
			</Inline>
		</Inline>
	);
}
